import React, { useState } from 'react';

import { defineMessages } from 'react-intl';

import { Spotlight, SpotlightTransition } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import { StatsigFeatureKeys } from '@atlassian/bitbucket-features';

import { useStatsigGate } from 'src/contexts/feature-provider';
import { useIntl } from 'src/hooks/intl';
import { usePref } from 'src/hooks/use-pref';

const messages = defineMessages({
  codeSuggestionIconHeader: {
    id: 'frontbucket.pullRequest.codeSuggestionIcon.header',
    description: 'Header for code suggestion icon spotlight',
    defaultMessage: 'Suggest a code change',
  },
  codeSuggestionIconBody: {
    id: 'frontbucket.pullRequest.codeSuggestionIcon.body',
    description: 'Body for code suggestion icon spotlight',
    defaultMessage:
      'Select this button or enter the ‘/suggest’ command to suggest a code change.',
  },
  codeSuggestionMakeSuggestionHeader: {
    id: 'frontbucket.pullRequest.codeSuggestionMakeSuggestion.header',
    description: 'Header for make suggestion spotlight',
    defaultMessage: 'Make your suggestion',
  },
  codeSuggestionMakeSuggestionBodyPart1: {
    id: 'frontbucket.pullRequest.codeSuggestionMakeSuggestion.body1',
    description: 'Body part 1 for make suggestion spotlight',
    defaultMessage: 'Edit the line of code with your suggested change.',
  },
  codeSuggestionMakeSuggestionBodyPart2: {
    id: 'frontbucket.pullRequest.codeSuggestionMakeSuggestion.body2',
    description: 'Body part 2 for make suggestion spotlight',
    defaultMessage:
      'Once you’ve saved your comment, the author can easily apply the code suggestion right here on the pull request.',
  },
  dismiss: {
    id: 'frontbucket.pullRequest.button.dismiss',
    description: 'Text to display on the button to dismiss the spotlight tour.',
    defaultMessage: 'Dismiss',
  },
  next: {
    id: 'frontbucket.pullRequest.button.next',
    description:
      'Text to display on the button to go to the next spotlight tour step.',
    defaultMessage: 'Next',
  },
  goBack: {
    id: 'frontbucket.pullRequest.button.goBack',
    description:
      'Text to display on the button to go back to the previous spotlight tour step.',
    defaultMessage: 'Go back',
  },
  exitTour: {
    id: 'frontbucket.pullRequest.button.exitTour',
    description: 'Text to display on the button to exit the spotlight tour.',
    defaultMessage: 'Exit tour',
  },
});

export const CodeSuggestionsSpotlight = (): JSX.Element | null => {
  const intl = useIntl();
  const areCodeSuggestionsEnabled = useStatsigGate(
    StatsigFeatureKeys.codeSuggestionsEditMode
  );
  const [activeSpotlight, setActiveSpotlight] = useState<null | number>(0);
  const [spotlightTourSeen, updatePref, isLoading] = usePref(
    'code-suggestions-educational-onboarding-seen'
  );

  if (
    !areCodeSuggestionsEnabled ||
    spotlightTourSeen ||
    isLoading ||
    activeSpotlight === null
  ) {
    return null;
  }

  const next = () => setActiveSpotlight((activeSpotlight || 0) + 1);

  const handleDismiss = () => {
    updatePref('true');
    setActiveSpotlight(null);
  };

  const renderActiveSpotlight = () => {
    const spotlights = [
      <Spotlight
        shouldWatchTarget
        target="code-suggestions-icon"
        targetBgColor={token('elevation.surface')}
        key="code-suggestions-icon"
        dialogPlacement="top center"
        dialogWidth={275}
        heading={intl.formatMessage(messages.codeSuggestionIconHeader)}
        actions={[
          {
            onClick: next,
            text: intl.formatMessage(messages.next),
          },
          {
            onClick: handleDismiss,
            text: intl.formatMessage(messages.dismiss),
            appearance: 'subtle',
          },
        ]}
        actionsBeforeElement="1/2"
      >
        {intl.formatMessage(messages.codeSuggestionIconBody)}
      </Spotlight>,
      <Spotlight
        shouldWatchTarget
        target="code-suggestions-icon"
        targetBgColor={token('elevation.surface')}
        key="code-suggestions-icon"
        dialogPlacement="top center"
        dialogWidth={275}
        heading={intl.formatMessage(
          messages.codeSuggestionMakeSuggestionHeader
        )}
        actions={[
          {
            onClick: handleDismiss,
            text: intl.formatMessage(messages.exitTour),
          },
        ]}
        actionsBeforeElement="2/2"
      >
        {intl.formatMessage(messages.codeSuggestionMakeSuggestionBodyPart1)}
        <br />
        <br />
        {intl.formatMessage(messages.codeSuggestionMakeSuggestionBodyPart2)}
      </Spotlight>,
    ];

    if (activeSpotlight === null) {
      return null;
    }

    return spotlights[activeSpotlight];
  };

  return <SpotlightTransition>{renderActiveSpotlight()}</SpotlightTransition>;
};
